'use client';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import { IconArrowRight, IconCheck, IconLink, IconPhoto } from '@tabler/icons-react';
import { BaseLink } from '@/navigation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TextInput } from '@/components/ui/text-input';
import { normalizeSpecialistUrl } from '@/utils/normalizeSpecialistUrl';
import { useAuthorRecommendationsCreate } from '@/generated';
import { toast } from 'sonner';

type FormValues = {
  url: string;
};

const RULES_HREF = '/agreement';

const ENTER =
  'animate-in fade-in slide-in-from-bottom-3 duration-500 ease-out [animation-fill-mode:both] motion-reduce:animate-none';

const delay = (ms: number) => ({ animationDelay: `${ms}ms` });

export function RecommendSpecialistClient() {
  const t = useTranslations('recommendSpecialistPage');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const schema = useMemo(
    () =>
      yup.object({
        url: yup
          .string()
          .trim()
          .required(t('urlRequired'))
          .test('url', t('urlInvalid'), (value) => !value || normalizeSpecialistUrl(value) !== null),
      }),
    [t],
  );

  const {
    handleSubmit,
    register,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { url: '' },
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const { mutateAsync: createRecommendation } = useAuthorRecommendationsCreate();

  const onSubmit = async ({ url }: FormValues) => {
    const normalizedUrl = normalizeSpecialistUrl(url);
    if (!normalizedUrl) {
      setError('url', { message: t('urlInvalid') });
      return;
    }

    try {
      await createRecommendation({ data: { url: normalizedUrl } });
      setIsSubmitted(true);
    } catch (error) {
      const status = (error as { status?: number })?.status;
      if (status === 400) {
        setError('url', { message: t('urlInvalid') }, { shouldFocus: true });
      } else if (status === 429) {
        setError('url', { message: t('tooManyRequests') });
      } else {
        toast.error(t('submitError'));
      }
    }
  };

  const onRecommendAnother = () => {
    reset({ url: '' });
    setIsSubmitted(false);
  };

  return (
    <section className='mx-auto w-full max-w-[980px] rounded-[16px] bg-bg-day px-[16px] py-[24px] shadow-[0_3px_12px_0_rgba(40,65,155,0.08)] sm:px-[24px] sm:py-[32px] md:rounded-[24px] md:px-[40px] md:py-[40px] lg:px-[64px] lg:py-[48px]'>
      {isSubmitted ? (
        <div className='mx-auto flex max-w-[620px] flex-col items-center text-center'>
          <div className='flex size-[96px] items-center justify-center rounded-full bg-primary-tint/15 animate-in fade-in zoom-in-75 duration-500 ease-out [animation-fill-mode:both] motion-reduce:animate-none md:size-[112px] lg:size-[128px]'>
            <div
              className='flex size-[66px] items-center justify-center rounded-full bg-primary-green-day animate-in zoom-in-50 duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] [animation-fill-mode:both] motion-reduce:animate-none md:size-[78px] lg:size-[90px]'
              style={delay(120)}
            >
              <IconCheck
                className='size-[38px] text-white animate-in fade-in zoom-in-0 spin-in-[-30deg] duration-400 ease-out [animation-fill-mode:both] motion-reduce:animate-none md:size-[44px] lg:size-[50px]'
                style={delay(320)}
                stroke={3}
              />
            </div>
          </div>

          <h1
            className={cn(
              ENTER,
              'mt-[16px] text-[22px] font-semibold leading-[1.2] text-text-primary-day sm:text-[24px] md:mt-[20px] md:text-[28px] lg:text-[32px]',
            )}
            style={delay(200)}
          >
            {t('successTitle')}
          </h1>
          <p
            className={cn(
              ENTER,
              'mt-[12px] text-[15px] leading-[1.6] text-text-disabled-day md:mt-[16px] md:text-[16px] lg:text-[18px]',
            )}
            style={delay(280)}
          >
            {t('successText')}
          </p>

          <Button
            type='button'
            fullWidth
            onClick={onRecommendAnother}
            className={cn(
              ENTER,
              'group mt-[24px] h-auto min-h-[48px] whitespace-normal py-[12px] md:mt-[32px] md:min-h-[52px] md:max-w-[520px] lg:min-h-[56px] lg:text-[17px]',
            )}
            style={delay(360)}
          >
            {t('recommendAnother')}
            <IconArrowRight className='size-5 transition-transform duration-200 group-hover:translate-x-[3px] rtl:-scale-x-100 rtl:group-hover:-translate-x-[3px]' />
          </Button>

          <BaseLink
            href='/'
            className={cn(
              ENTER,
              'mt-[16px] text-[15px] text-main-green underline-offset-4 transition-colors hover:underline md:mt-[20px] md:text-[16px]',
            )}
            style={delay(440)}
          >
            {t('backHome')}
          </BaseLink>
        </div>
      ) : (
        <div className='mx-auto flex max-w-[760px] flex-col items-center text-center'>
          <div className='flex size-[72px] items-center justify-center rounded-full bg-primary-tint/15 animate-in fade-in zoom-in-75 duration-500 ease-out [animation-fill-mode:both] motion-reduce:animate-none md:size-[84px] lg:size-[96px]'>
            <IconPhoto className='size-[34px] text-primary-green-day md:size-[40px] lg:size-[46px]' stroke={1.5} />
          </div>

          <h1
            className={cn(
              ENTER,
              'mt-[16px] text-[22px] font-semibold leading-[1.2] text-text-primary-day sm:text-[24px] md:mt-[20px] md:text-[28px] lg:text-[32px]',
            )}
            style={delay(80)}
          >
            {t('title')}
          </h1>
          <p
            className={cn(
              ENTER,
              'mt-[12px] text-[15px] leading-[1.6] text-text-primary-day/80 md:mt-[16px] md:text-[16px] lg:text-[18px]',
            )}
            style={delay(160)}
          >
            {t('lead')}
          </p>
          <p
            className={cn(
              ENTER,
              'mt-[10px] text-[14px] leading-[1.6] text-text-disabled-day md:mt-[12px] lg:text-[16px]',
            )}
            style={delay(240)}
          >
            {t.rich('rulesNote', {
              rules: (chunks) => (
                <BaseLink
                  href={RULES_HREF}
                  target='_blank'
                  className='text-main-green underline underline-offset-2 transition-colors hover:text-main-green/80'
                >
                  {chunks}
                </BaseLink>
              ),
            })}
          </p>

          <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            className={cn(
              ENTER,
              'mt-[24px] flex w-full max-w-[640px] flex-col gap-[16px] text-start md:mt-[32px] md:gap-[20px]',
            )}
            style={delay(320)}
          >
            <div className='flex flex-col gap-[8px]'>
              <Label
                htmlFor='specialist-url'
                className='text-[14px] font-semibold text-text-primary-day md:text-[15px]'
              >
                {t('urlLabel')}
              </Label>
              <TextInput
                id='specialist-url'
                type='url'
                inputMode='url'
                autoComplete='url'
                ltrField
                placeholder={t('urlPlaceholder')}
                leftIcon={<IconLink className='size-[22px] text-icon-default' stroke={1.5} />}
                {...register('url')}
                error={errors.url?.message}
              />
            </div>

            <Button
              type='submit'
              fullWidth
              loading={isSubmitting}
              className='group h-auto min-h-[48px] whitespace-normal py-[12px] md:min-h-[52px] lg:min-h-[56px] lg:text-[17px]'
            >
              {t('submit')}
              <IconArrowRight className='size-5 transition-transform duration-200 group-hover:translate-x-[3px] rtl:-scale-x-100 rtl:group-hover:-translate-x-[3px]' />
            </Button>
          </form>
        </div>
      )}
    </section>
  );
}
